import { has, keyBy, orderBy } from 'lodash';
import { LEARNING_ASSET_DURATIONS, LICENSE_TYPES } from '../Constants';
import { rankAccessor, subscriptionTypeAccessor, givenBehaviorAccessor, lockedItemAccessor } from './AccessorUtils';

export const getCollectionMetadata = (learningAssets) => {
    const metaData = {
        totalTime: 0,
        items: {},
        points: {}
    };

    learningAssets.forEach((asset) => {
        // compatibility for learning assets and user learning assets
        const baseAsset = has(asset, 'track') ? asset.track : asset;

        const timeInMinutes = baseAsset.durationId ? LEARNING_ASSET_DURATIONS()[baseAsset.durationId].inMinutes : 0;

        metaData.totalTime += timeInMinutes;

        if (baseAsset.type) {
            metaData.items[baseAsset.type] = metaData.items[baseAsset.type] ? metaData.items[baseAsset.type] + 1 : 1;
        }
    });

    return metaData;
};

export const filterCollectionsForVerbAdmin = (collections, companyCollectionLicenses, hiddenCollections) => {
    const licensesByCollectionId = keyBy(companyCollectionLicenses, 'collectionId');
    const hiddenCollectionsById = keyBy(hiddenCollections, 'id');

    let licenseType;
    return collections.filter((collection) => {
        licenseType = collection.subscriptionType || '';
        if (
            (licenseType.toLocaleUpperCase() === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE_UPPER &&
                !(collection._id in licensesByCollectionId)) ||
            collection._id in hiddenCollectionsById
        ) {
            return false;
        }

        return !!collection.isActive;
    });
};

export const filterHiddenCollections = (collections, hiddenCollections = []) => {
    const hiddenCollectionsById = keyBy(hiddenCollections, 'id');
    return collections.filter((collection) => {
        if (collection._id in hiddenCollectionsById) {
            return false;
        }
        return true;
    });
};

export const filterLockedCollections = (collections) => collections.filter((collection) => !collection.isLocked);

export const filterCollectionsForCompany = (collections, userCompany) => {
    const { collectionLicenses = [], hiddenCollections = [] } = userCompany;
    const companyCollectionLicenseLookup = keyBy(collectionLicenses, 'collectionId');
    const hiddenCollectionLookup = keyBy(hiddenCollections, 'id');

    return collections.filter((collection) => {
        if (collection.isActive && !(collection._id in hiddenCollectionLookup)) {
            if (collection.subscriptionType === 'AvailableForAll') {
                return true;
            }
            if (
                collection.subscriptionType === 'ExclusiveCompanyLicense' &&
                companyCollectionLicenseLookup[collection._id]
            ) {
                return true;
            }
        }
        return false;
    });
};

export const sortCollections = (collections) =>
    collections.sort(
        (a, b) =>
            b.isActive - a.isActive ||
            (a.name.toLowerCase() > b.name.toLowerCase()) - (a.name.toLowerCase() < b.name.toLowerCase())
    );

export const sortDate = (collections, dateProp) =>
    collections.sort((item1, item2) => {
        const date1 = new Date(item1[dateProp] || '0');
        const date2 = new Date(item2[dateProp] || '0');
        if (date1 > date2) {
            return -1;
        }
        return 1;
    });

export const assignTeamDetails = (collections, teamState) => {
    const collectionLookup = {};
    if (!teamState) {
        return collections;
    }
    const teams = Object.values(teamState.byId);

    for (let i = 0; i < teams.length; i += 1) {
        const team = teams[i];

        if (team.recommendedCollections) {
            for (let j = 0; j < team.recommendedCollections.length; j += 1) {
                const recommendation = team.recommendedCollections[j];

                if (typeof recommendation === 'string') {
                    collectionLookup[recommendation] = 1;
                } else if (typeof recommendation === 'object') {
                    collectionLookup[recommendation.collectionId] = 1;
                }
            }
        }
    }
    return collections.map((collection) => {
        if (collectionLookup[collection._id] || collectionLookup[collection.collectionId]) {
            collection.recommendationInfo = collection.recommendationInfo || { code: 'ASSIGNED' };
        }
        return collection;
    });
};

export const sortByRank = (collections) => orderBy(collections, [lockedItemAccessor, rankAccessor], ['asc', 'asc']);

export const sortByCompanyLicense = (collections) =>
    orderBy(collections, [subscriptionTypeAccessor, rankAccessor], ['desc', 'asc']);

export const sortBySubscriptionTypeAndRank = (collections) =>
    orderBy(collections, [lockedItemAccessor, subscriptionTypeAccessor, rankAccessor], ['asc', 'desc', 'asc']);

export const sortByGivenBehaviorAndDate = (collections, behaviorId) =>
    orderBy(collections, [givenBehaviorAccessor(behaviorId), 'createdOn'], ['desc', 'desc']);

export const sortByLocked = (collections) =>
    orderBy(collections, [lockedItemAccessor, subscriptionTypeAccessor, rankAccessor], ['asc', 'desc', 'asc']);

export const isShowable = (subscriptionType) =>
    (subscriptionType || '').toLocaleUpperCase() !== LICENSE_TYPES.NOT_AVAILABLE_UPPER;
